import { useState, useEffect } from 'react';
import { siteSettingsAPI } from '../../services/api';
import { useSiteSettings } from '../../context/SiteSettingsContext';
import { Send, Plus, X, GripVertical } from 'lucide-react';

export default function SiteSettingsAdmin() {
    const { refreshSettings } = useSiteSettings();
    const [form, setForm] = useState({
        phone: '', whatsapp: '', email: '', address: '',
        instagram: '', twitter: '', linkedin: '', facebook: '', youtube: '',
        workingHours: '', mapsEmbedUrl: '',
        telegramBotToken: '', telegramChatId: '',
        marqueeItems: '[]'
    });
    const [marqueeList, setMarqueeList] = useState([]);
    const [newMarquee, setNewMarquee] = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState('');
    const [telegramTesting, setTelegramTesting] = useState(false);
    const [telegramMsg, setTelegramMsg] = useState('');

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const res = await siteSettingsAPI.getAll();
                setForm(prev => ({ ...prev, ...res.data }));
                try {
                    const items = JSON.parse(res.data.marqueeItems || '[]');
                    if (Array.isArray(items)) setMarqueeList(items);
                } catch {}
            } catch (e) { console.error(e); }
            setLoading(false);
        };
        fetchSettings();
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setMessage('');
        try {
            await siteSettingsAPI.update({ ...form, marqueeItems: JSON.stringify(marqueeList) });
            await refreshSettings();
            setMessage('Ayarlar başarıyla kaydedildi!');
            setTimeout(() => setMessage(''), 3000);
        } catch (err) {
            alert(err.response?.data?.error || 'Kayıt hatası');
        }
        setSaving(false);
    };

    const update = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

    const addMarqueeItem = () => {
        const text = newMarquee.trim();
        if (!text) return;
        setMarqueeList(prev => [...prev, text]);
        setNewMarquee('');
    };

    const removeMarqueeItem = (index) => {
        setMarqueeList(prev => prev.filter((_, i) => i !== index));
    };

    const moveMarqueeItem = (index, dir) => {
        setMarqueeList(prev => {
            const arr = [...prev];
            const target = index + dir;
            if (target < 0 || target >= arr.length) return arr;
            [arr[index], arr[target]] = [arr[target], arr[index]];
            return arr;
        });
    };

    const handleTelegramTest = async () => {
        if (!form.telegramBotToken || !form.telegramChatId) {
            setTelegramMsg('Önce Bot Token ve Chat ID giriniz ve kaydediniz.');
            setTimeout(() => setTelegramMsg(''), 3000);
            return;
        }
        setTelegramTesting(true);
        setTelegramMsg('');
        try {
            await siteSettingsAPI.testTelegram();
            setTelegramMsg('Test mesajı başarıyla gönderildi!');
        } catch (err) {
            setTelegramMsg(err.response?.data?.error || 'Test mesajı gönderilemedi');
        }
        setTelegramTesting(false);
        setTimeout(() => setTelegramMsg(''), 5000);
    };

    if (loading) return <div className="loading"><div className="spinner"></div></div>;

    return (
        <div>
            <div className="admin-header">
                <h1>Site Ayarları</h1>
            </div>

            <form onSubmit={handleSubmit}>
                <div style={{ display: 'grid', gap: '2rem', maxWidth: 800 }}>
                    {/* İletişim */}
                    <div style={{ background: 'var(--card-bg)', borderRadius: 12, padding: '1.5rem', border: '1px solid var(--border)' }}>
                        <h3 style={{ marginBottom: '1rem', fontSize: '1.1rem' }}>İletişim Bilgileri</h3>
                        <div style={{ display: 'grid', gap: '1rem' }}>
                            <div className="form-group">
                                <label>Telefon</label>
                                <input className="form-control" value={form.phone} onChange={e => update('phone', e.target.value)} placeholder="+90 555 123 45 67" />
                            </div>
                            <div className="form-group">
                                <label>WhatsApp Numarası (ülke kodu ile, boşluksuz)</label>
                                <input className="form-control" value={form.whatsapp} onChange={e => update('whatsapp', e.target.value)} placeholder="905551234567" />
                            </div>
                            <div className="form-group">
                                <label>E-posta</label>
                                <input type="email" className="form-control" value={form.email} onChange={e => update('email', e.target.value)} placeholder="info@example.com" />
                            </div>
                            <div className="form-group">
                                <label>Adres</label>
                                <textarea className="form-control" rows={2} value={form.address} onChange={e => update('address', e.target.value)} />
                            </div>
                        </div>
                    </div>

                    {/* Sosyal Medya */}
                    <div style={{ background: 'var(--card-bg)', borderRadius: 12, padding: '1.5rem', border: '1px solid var(--border)' }}>
                        <h3 style={{ marginBottom: '1rem', fontSize: '1.1rem' }}>Sosyal Medya</h3>
                        <div style={{ display: 'grid', gap: '1rem' }}>
                            <div className="form-group">
                                <label>Instagram URL</label>
                                <input className="form-control" value={form.instagram} onChange={e => update('instagram', e.target.value)} placeholder="https://instagram.com/..." />
                            </div>
                            <div className="form-group">
                                <label>Twitter / X URL</label>
                                <input className="form-control" value={form.twitter} onChange={e => update('twitter', e.target.value)} placeholder="https://twitter.com/..." />
                            </div>
                            <div className="form-group">
                                <label>LinkedIn URL</label>
                                <input className="form-control" value={form.linkedin} onChange={e => update('linkedin', e.target.value)} placeholder="https://linkedin.com/..." />
                            </div>
                            <div className="form-group">
                                <label>Facebook URL</label>
                                <input className="form-control" value={form.facebook} onChange={e => update('facebook', e.target.value)} placeholder="https://facebook.com/..." />
                            </div>
                            <div className="form-group">
                                <label>YouTube URL</label>
                                <input className="form-control" value={form.youtube} onChange={e => update('youtube', e.target.value)} placeholder="https://youtube.com/..." />
                            </div>
                        </div>
                    </div>

                    {/* Telegram Bot */}
                    <div style={{ background: 'var(--card-bg)', borderRadius: 12, padding: '1.5rem', border: '1px solid var(--border)' }}>
                        <h3 style={{ marginBottom: '0.5rem', fontSize: '1.1rem' }}>Telegram Bildirimleri</h3>
                        <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '1rem' }}>
                            Sipariş, stok ve yorum bildirimlerini Telegram'a gönderir. BotFather'dan bot oluşturup token alın, ardından botunuza mesaj atıp Chat ID'nizi öğrenin.
                        </p>
                        <div style={{ display: 'grid', gap: '1rem' }}>
                            <div className="form-group">
                                <label>Bot Token</label>
                                <input className="form-control" value={form.telegramBotToken} onChange={e => update('telegramBotToken', e.target.value)} placeholder="123456789:ABCdefGhIJKlmNoPQRsTUVwxyz" />
                            </div>
                            <div className="form-group">
                                <label>Chat ID</label>
                                <input className="form-control" value={form.telegramChatId} onChange={e => update('telegramChatId', e.target.value)} placeholder="-1001234567890 veya 123456789" />
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                <button type="button" className="btn btn-secondary" onClick={handleTelegramTest} disabled={telegramTesting} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    <Send size={16} />
                                    {telegramTesting ? 'Gönderiliyor...' : 'Test Mesajı Gönder'}
                                </button>
                                {telegramMsg && <span style={{ fontSize: '0.85rem', color: telegramMsg.includes('başarı') ? 'var(--success)' : 'var(--danger)', fontWeight: 500 }}>{telegramMsg}</span>}
                            </div>
                        </div>
                    </div>

                    {/* Kayan Yazı Şeridi */}
                    <div style={{ background: 'var(--card-bg)', borderRadius: 12, padding: '1.5rem', border: '1px solid var(--border)' }}>
                        <h3 style={{ marginBottom: '0.5rem', fontSize: '1.1rem' }}>Kayan Yazı Şeridi (Marquee)</h3>
                        <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '1rem' }}>
                            Anasayfada hero bölümünün altında kayan yazılar. Sırayı oklar ile değiştirebilirsiniz.
                        </p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '12px' }}>
                            {marqueeList.map((item, i) => (
                                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'var(--bg-secondary, #f5f5f5)', padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border)' }}>
                                    <GripVertical size={14} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                                    <span style={{ flex: 1, fontSize: '0.9rem' }}>{item}</span>
                                    <button type="button" onClick={() => moveMarqueeItem(i, -1)} disabled={i === 0} style={{ background: 'none', border: 'none', cursor: i === 0 ? 'default' : 'pointer', opacity: i === 0 ? 0.3 : 1, padding: '2px 6px', fontSize: '1rem' }}>&#9650;</button>
                                    <button type="button" onClick={() => moveMarqueeItem(i, 1)} disabled={i === marqueeList.length - 1} style={{ background: 'none', border: 'none', cursor: i === marqueeList.length - 1 ? 'default' : 'pointer', opacity: i === marqueeList.length - 1 ? 0.3 : 1, padding: '2px 6px', fontSize: '1rem' }}>&#9660;</button>
                                    <button type="button" onClick={() => removeMarqueeItem(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--danger, #ef4444)', padding: '2px', display: 'flex' }}><X size={16} /></button>
                                </div>
                            ))}
                            {marqueeList.length === 0 && (
                                <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', fontStyle: 'italic' }}>Henüz öğe eklenmemiş. Aşağıdan ekleyin.</p>
                            )}
                        </div>
                        <div style={{ display: 'flex', gap: '8px' }}>
                            <input
                                className="form-control"
                                value={newMarquee}
                                onChange={e => setNewMarquee(e.target.value)}
                                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addMarqueeItem(); } }}
                                placeholder="Yeni öğe yazın..."
                                style={{ flex: 1 }}
                            />
                            <button type="button" className="btn btn-secondary" onClick={addMarqueeItem} style={{ display: 'flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap' }}>
                                <Plus size={16} /> Ekle
                            </button>
                        </div>
                    </div>

                    {/* Diğer */}
                    <div style={{ background: 'var(--card-bg)', borderRadius: 12, padding: '1.5rem', border: '1px solid var(--border)' }}>
                        <h3 style={{ marginBottom: '1rem', fontSize: '1.1rem' }}>Diğer Ayarlar</h3>
                        <div style={{ display: 'grid', gap: '1rem' }}>
                            <div className="form-group">
                                <label>Çalışma Saatleri</label>
                                <input className="form-control" value={form.workingHours} onChange={e => update('workingHours', e.target.value)} placeholder="Pazartesi - Cumartesi: 09:00 - 18:00" />
                            </div>
                            <div className="form-group">
                                <label>Google Maps Embed URL</label>
                                <input className="form-control" value={form.mapsEmbedUrl} onChange={e => update('mapsEmbedUrl', e.target.value)} placeholder="https://www.google.com/maps/embed?pb=..." />
                            </div>
                        </div>
                    </div>
                </div>

                <div style={{ marginTop: '1.5rem', display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    <button type="submit" className="btn btn-primary" disabled={saving}>
                        {saving ? 'Kaydediliyor...' : 'Kaydet'}
                    </button>
                    {message && <span style={{ color: 'var(--success)', fontWeight: 600 }}>{message}</span>}
                </div>
            </form>
        </div>
    );
}
